import { Fragment, FunctionComponent, ReactNode, useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";

import { Dialog } from "primereact/dialog";

import { ActionButton } from "../../../Buttons";
import { SharedDialogStateContext } from "./sharedDialogHook";
import { sharedDialog } from "./SharedDialogService";
import {
  ISharedDialogAction,
  ISharedDialogDismissBehavior,
  SharedDialogSize,
  TSharedDialogInstance,
} from "./sharedDialogTypes";

import "./SharedDialogProvider.scss";

interface ISharedDialogProps {
  children: ReactNode;
}

const DEFAULT_SIZE: SharedDialogSize = "medium";
const DEFAULT_DISMISS_BEHAVIOR: ISharedDialogDismissBehavior = {
  closeIcon: true,
  escape: true,
  outsideClick: false,
};

const SIZE_STYLES: Record<SharedDialogSize, { maxWidth: string; breakpoints: Record<string, string> }> = {
  small: {
    maxWidth: "40rem",
    breakpoints: { "1200px": "40vw", "1024px": "50vw", "768px": "95vw" },
  },
  medium: {
    maxWidth: "50rem",
    breakpoints: { "1200px": "60vw", "1024px": "65vw", "768px": "95vw" },
  },
  large: {
    maxWidth: "60rem",
    breakpoints: { "1024px": "80vw", "768px": "95vw" },
  },
  largest: {
    maxWidth: "80rem",
    breakpoints: { "1024px": "80vw", "768px": "95vw" },
  },
};

export const SharedDialogProvider: FunctionComponent<ISharedDialogProps> = ({ children }: ISharedDialogProps) => {
  const [activeDialog, setActiveDialog] = useState<TSharedDialogInstance | undefined>();
  const [sharedState, setSharedState] = useState<unknown>(undefined);
  const activeDialogRef = useRef<TSharedDialogInstance | undefined>(undefined);

  const doAdditionalActionsOnDialogClose = (dialog: TSharedDialogInstance | undefined): void => {
    if (!dialog?.onClose) {
      return;
    }

    queueMicrotask(() => {
      dialog.onClose?.();
    });
  };

  const unmountDialog = useCallback((id: string): void => {
    const previousDialog = activeDialogRef.current;

    // ignores close requests for a new dialog when it has already replaced the previous dialog.
    if (previousDialog?.id !== id) {
      return;
    }

    activeDialogRef.current = undefined;
    setSharedState(undefined);
    setActiveDialog(undefined);
    doAdditionalActionsOnDialogClose(previousDialog);
  }, []);

  const mountDialog = useCallback((nextDialog: TSharedDialogInstance, nextSharedState: unknown): void => {
    const previousDialog = activeDialogRef.current;

    activeDialogRef.current = nextDialog;
    setSharedState(nextSharedState);
    setActiveDialog(nextDialog);
    doAdditionalActionsOnDialogClose(previousDialog);
  }, []);

  const handleActionClick = useCallback(
    async (dialogId: string, action: ISharedDialogAction): Promise<void> => {
      try {
        await action.onClick?.({
          id: dialogId,
          sharedState,
          setSharedDialogState: setSharedState,
        });
      } finally {
        if (action.closeOnAction) {
          unmountDialog(dialogId);
        }
      }
    },
    [sharedState, unmountDialog],
  );

  useLayoutEffect(() => {
    sharedDialog.setSharedDialogApi({
      mountDialog,
      unmountDialog,
    });

    return () => {
      sharedDialog.setSharedDialogApi(undefined);
    };
  }, [mountDialog, unmountDialog]);

  const dismissBehavior = {
    ...DEFAULT_DISMISS_BEHAVIOR,
    ...activeDialog?.customDismissBehavior,
  };

  const sizeStyle = SIZE_STYLES[activeDialog?.size ?? DEFAULT_SIZE];

  const dialogContext = useMemo(() => {
    if (!activeDialog) {
      return undefined;
    }

    return {
      id: activeDialog.id,
      sharedState,
      setSharedDialogState: setSharedState,
    };
  }, [activeDialog, sharedState]);

  const content = useMemo(() => {
    if (!activeDialog) {
      return null;
    }

    return (
      (typeof activeDialog.customContent === "function"
        ? activeDialog.customContent(dialogContext!)
        : activeDialog.customContent) ?? null
    );
  }, [activeDialog, dialogContext]);

  const onFooterActionClick = useCallback(
    (action: ISharedDialogAction): void => {
      if (!activeDialog) {
        return;
      }

      void handleActionClick(activeDialog.id, action);
    },
    [handleActionClick, activeDialog],
  );

  const footer = useMemo(() => {
    if (!activeDialog) {
      return null;
    }

    if (activeDialog.customFooter) {
      return (
        (typeof activeDialog.customFooter === "function"
          ? activeDialog.customFooter(dialogContext!)
          : activeDialog.customFooter) ?? null
      );
    }

    if (!activeDialog.footerActions) {
      return null;
    }

    const resolvedFooterActions =
      (typeof activeDialog.footerActions === "function"
        ? activeDialog.footerActions(dialogContext!)
        : activeDialog.footerActions) ?? undefined;

    if (!resolvedFooterActions?.length) {
      return null;
    }

    return (
      <div className="shared-dialog-actions">
        {resolvedFooterActions.map(action => (
          <ActionButton
            key={action.label}
            id={action.label}
            label={action.label}
            showBackground={!action.isTextButton}
            buttonDisabled={action.isDisabled}
            icon={action.isLoading ? undefined : undefined}
            isLoading={action.isLoading}
            tooltip={action.tooltip}
            tooltipPosition={action.tooltipOptions?.position}
            tooltipDisabled={!action.tooltipOptions?.showOnDisabled && action.isDisabled}
            onClick={() => onFooterActionClick(action)}
          />
        ))}
      </div>
    );
  }, [onFooterActionClick, activeDialog, dialogContext]);

  return (
    <Fragment>
      {children}
      {activeDialog ? (
        <SharedDialogStateContext value={{ sharedDialogState: sharedState, setSharedDialogState: setSharedState }}>
          <Dialog
            visible={true}
            header={activeDialog.title}
            footer={footer}
            className={["shared-dialog", activeDialog.className].filter(Boolean).join(" ")}
            modal={true}
            blockScroll={true}
            closable={dismissBehavior.closeIcon}
            closeOnEscape={dismissBehavior.escape}
            dismissableMask={dismissBehavior.outsideClick}
            draggable={false}
            resizable={false}
            focusOnShow={true}
            baseZIndex={1200}
            breakpoints={sizeStyle.breakpoints}
            style={{ width: sizeStyle.maxWidth, maxWidth: sizeStyle.maxWidth }}
            onHide={() => unmountDialog(activeDialog.id)}
          >
            {content}
          </Dialog>
        </SharedDialogStateContext>
      ) : null}
    </Fragment>
  );
};
